import { useEffect, useMemo, useState, type FormEvent } from 'react';
import { useAuth } from 'auth-sso-kit/client/index.js';

/**
 * The split login page. Pre-auth and outside the app shell, so it is its own visual world
 * (styles: public/styles/main.css, section "LOGIN — the split page", everything under `.lg-`).
 * A full-bleed photograph carries the NAVEN mark, the signet wordmark and the tagline; the
 * paper column carries the sign-in and the password-reset request.
 *
 * LANGUAGE: pre-auth there is no operator preference, so the page follows the BROWSER —
 * English, German or French — from its own dictionary below (the app's i18n stores only
 * carry en/de and the operator's stored choice, which is unknown before sign-in).
 *
 * Behaviour replicates the kit's LoginPage (lib/auth-sso-kit — untouchable):
 *   · `authMethod === 'oidc'` → a single SSO button; otherwise the e-mail + password form
 *   · `#error=not_authorized | oidc_failed | no_email` becomes a message once, hash cleared
 *   · empty e-mail / password are rejected before calling `login`
 */
export interface LoginPageProps {
  /** The wordmark on the image panel (rendered lowercase, dot on the baseline). */
  title?: string;
}

type Lg = 'en' | 'de' | 'fr';
const pickLang = (): Lg => {
  const l = (typeof navigator !== 'undefined' ? navigator.language : 'en').slice(0, 2).toLowerCase();
  return l === 'de' ? 'de' : l === 'fr' ? 'fr' : 'en';
};

const STR = {
  en: {
    eyebrow: 'Engagement control',
    tagline: 'Overview of project requirements\nand engagement status',
    service: 'A service by naven.ch',
    signin: 'Sign in',
    signingin: 'Signing in…',
    lede: 'Access to the engagement control of your Naven project',
    email: 'Email',
    password: 'Password',
    emailPh: 'your@email.com',
    passwordPh: 'Enter your password',
    sso: 'Log in with SSO',
    emailRequired: 'Email is required',
    passwordRequired: 'Password is required',
    loginFailed: 'Login failed',
    notAuthorized: 'Your email is not authorized to access this application. Contact an administrator.',
    authFailed: 'Authentication failed. Please try again.',
    noEmail: 'No email was received from the identity provider.',
    forgot: 'Forgot your password?',
    fpTitle: 'Request a password reset',
    fpBody: 'Your request goes to the operators of this installation. It is reviewed manually and may take up to 24 hours to be granted — you will be contacted at the address below.',
    fpSend: 'Send request',
    fpSending: 'Sending…',
    fpDone: 'Your request has been recorded. It is reviewed manually and may take up to 24 hours.',
    fpInvalid: 'A valid email address is required.',
    fpTooMany: 'Too many requests — please try again later.',
    cancel: 'Cancel',
    close: 'Close',
  },
  de: {
    eyebrow: 'Auftragssteuerung',
    tagline: 'Übersicht Projekt\u2011Anforderungen\nund Auftragsstatus',
    service: 'Ein Service von naven.ch',
    signin: 'Anmelden',
    signingin: 'Anmeldung läuft…',
    lede: 'Zugang zur Auftragssteuerung Ihres Naven-Projekts',
    email: 'E-Mail',
    password: 'Passwort',
    emailPh: 'ihre@email.ch',
    passwordPh: 'Passwort eingeben',
    sso: 'Mit SSO anmelden',
    emailRequired: 'E-Mail-Adresse ist erforderlich',
    passwordRequired: 'Passwort ist erforderlich',
    loginFailed: 'Anmeldung fehlgeschlagen',
    notAuthorized: 'Ihre E-Mail-Adresse ist für diese Anwendung nicht freigegeben. Wenden Sie sich an eine Administratorin oder einen Administrator.',
    authFailed: 'Die Anmeldung ist fehlgeschlagen. Bitte versuchen Sie es erneut.',
    noEmail: 'Vom Identitätsanbieter wurde keine E-Mail-Adresse übermittelt.',
    forgot: 'Passwort vergessen?',
    fpTitle: 'Passwort-Zurücksetzung beantragen',
    fpBody: 'Ihr Antrag geht an die Operatoren dieser Installation. Er wird manuell geprüft; die Freigabe kann bis zu 24 Stunden dauern – Sie werden über die untenstehende Adresse kontaktiert.',
    fpSend: 'Antrag senden',
    fpSending: 'Wird gesendet…',
    fpDone: 'Ihr Antrag ist erfasst. Er wird manuell geprüft und kann bis zu 24 Stunden dauern.',
    fpInvalid: 'Eine gültige E-Mail-Adresse ist erforderlich.',
    fpTooMany: 'Zu viele Anfragen – bitte versuchen Sie es später erneut.',
    cancel: 'Abbrechen',
    close: 'Schliessen',
  },
  fr: {
    eyebrow: 'Pilotage de mandat',
    tagline: 'Vue d’ensemble des exigences de projet\net de l’état du mandat',
    service: 'Un service de naven.ch',
    signin: 'Se connecter',
    signingin: 'Connexion…',
    lede: 'Accès au pilotage de votre projet Naven',
    email: 'E-mail',
    password: 'Mot de passe',
    emailPh: 'votre@email.ch',
    passwordPh: 'Saisissez votre mot de passe',
    sso: 'Se connecter via SSO',
    emailRequired: 'L’adresse e-mail est requise',
    passwordRequired: 'Le mot de passe est requis',
    loginFailed: 'Échec de la connexion',
    notAuthorized: 'Votre adresse e-mail n’est pas autorisée pour cette application. Contactez un administrateur.',
    authFailed: 'Échec de l’authentification. Veuillez réessayer.',
    noEmail: 'Aucune adresse e-mail n’a été transmise par le fournisseur d’identité.',
    forgot: 'Mot de passe oublié ?',
    fpTitle: 'Demander une réinitialisation du mot de passe',
    fpBody: 'Votre demande est transmise aux opérateurs de cette installation. Elle est examinée manuellement et peut prendre jusqu’à 24 heures – vous serez contacté à l’adresse ci-dessous.',
    fpSend: 'Envoyer la demande',
    fpSending: 'Envoi…',
    fpDone: 'Votre demande est enregistrée. Elle est examinée manuellement et peut prendre jusqu’à 24 heures.',
    fpInvalid: 'Une adresse e-mail valide est requise.',
    fpTooMany: 'Trop de demandes – veuillez réessayer plus tard.',
    cancel: 'Annuler',
    close: 'Fermer',
  },
} as const;

/* 1×1 transparent GIF: the <img> fallback below 641px, where the panel drops the
   photograph — phones never download the 650 KB webp. */
const BLANK = 'data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///yH5BAEAAAAALAAAAAABAAEAAAIBRAA7';

export function LoginPage({ title = 'Signet' }: LoginPageProps) {
  const L = useMemo(() => STR[pickLang()], []);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [fpOpen, setFpOpen] = useState(false);
  const [fpEmail, setFpEmail] = useState('');
  const [fpState, setFpState] = useState<'idle' | 'sending' | 'done' | 'error'>('idle');
  const [fpError, setFpError] = useState('');
  const { login, loginWithOIDC, authMethod } = useAuth();

  useEffect(() => {
    const hash = window.location.hash;
    if (hash.includes('error=not_authorized')) setError(L.notAuthorized);
    else if (hash.includes('error=oidc_failed')) setError(L.authFailed);
    else if (hash.includes('error=no_email')) setError(L.noEmail);
    if (hash.includes('error=')) window.location.hash = '';
  }, [L]);

  const handlePasswordSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (!email.trim()) { setError(L.emailRequired); return; }
    if (!password) { setError(L.passwordRequired); return; }
    setLoading(true);
    setError('');
    try {
      await login(email.trim(), password);
    } catch (err: any) {
      setError(err.message || L.loginFailed);
    } finally {
      setLoading(false);
    }
  };

  const openFp = () => { setFpEmail(email); setFpState('idle'); setFpError(''); setFpOpen(true); };
  const sendFp = async (e: FormEvent) => {
    e.preventDefault();
    const addr = fpEmail.trim();
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(addr)) { setFpState('error'); setFpError(L.fpInvalid); return; }
    setFpState('sending');
    try {
      const res = await fetch('/api/password-reset', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: addr }),
      });
      if (res.status === 429) { setFpState('error'); setFpError(L.fpTooMany); return; }
      if (!res.ok) { setFpState('error'); setFpError(L.fpInvalid); return; }
      setFpState('done');
    } catch {
      setFpState('error'); setFpError(L.fpTooMany);
    }
  };

  return (
    <main className="lg-root">
      <aside className="lg-brand" aria-label={title}>
        <picture className="lg-brand-media">
          <source media="(min-width: 641px)" srcSet="/img/login-vita.webp" type="image/webp" />
          <img src={BLANK} alt="" width={2048} height={1362} decoding="async" fetchPriority="high" />
        </picture>
        <div className="lg-scrim" aria-hidden="true" />
        <a className="lg-logo" href="https://naven.ch" aria-label="naven">NAVEN<i aria-hidden="true" /></a>

        <div className="lg-brand-stack">
          <p className="lg-eyebrow lg-enter">{L.eyebrow}</p>
          <p className="lg-wordmark lg-enter lg-d1">{title.toLowerCase()}<i aria-hidden="true" /></p>
          <p className="lg-tagline lg-enter lg-d2">{L.tagline}</p>
          <p className="lg-brand-foot lg-enter lg-d3">{L.service}</p>
        </div>
      </aside>

      <section className="lg-form" aria-labelledby="lg-heading">
        <div className="lg-card lg-enter lg-d1">
          <h1 id="lg-heading">{L.signin}</h1>
          <p className="lg-lede">{L.lede}</p>

          {error && (
            <div className="lg-error" role="alert">
              {error}
            </div>
          )}

          {authMethod === 'oidc' ? (
            <button type="button" className="lg-btn" onClick={loginWithOIDC}>
              {L.sso}
              <Arrow />
            </button>
          ) : (
            <form onSubmit={handlePasswordSubmit} noValidate>
              <div className="lg-field">
                <label className="lg-label" htmlFor="lg-email">{L.email}</label>
                <input
                  type="email"
                  id="lg-email"
                  name="email"
                  className="lg-input"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder={L.emailPh}
                  autoComplete="email"
                  autoFocus
                />
              </div>

              <div className="lg-field">
                <label className="lg-label" htmlFor="lg-password">{L.password}</label>
                <input
                  type="password"
                  id="lg-password"
                  name="password"
                  className="lg-input"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder={L.passwordPh}
                  autoComplete="current-password"
                />
              </div>

              <button type="submit" className="lg-btn" disabled={loading}>
                {loading ? L.signingin : L.signin}
                {!loading && <Arrow />}
              </button>
            </form>
          )}

          <p className="lg-foot">
            <button type="button" className="lg-linkbtn" onClick={openFp}>{L.forgot}</button>
          </p>
        </div>
      </section>

      {fpOpen && (
        <div className="lg-fp-backdrop" role="dialog" aria-modal="true" aria-labelledby="lg-fp-title" onClick={(e) => { if (e.target === e.currentTarget) setFpOpen(false); }}>
          <div className="lg-fp">
            <h2 id="lg-fp-title">{L.fpTitle}</h2>
            <p className="lg-fp-body">{L.fpBody}</p>
            {fpState === 'done' ? (
              <>
                <p className="lg-fp-done">{L.fpDone}</p>
                <div className="lg-fp-row">
                  <button type="button" className="lg-btn" onClick={() => setFpOpen(false)}>{L.close}</button>
                </div>
              </>
            ) : (
              <form onSubmit={sendFp} noValidate>
                <div className="lg-field">
                  <label className="lg-label" htmlFor="lg-fp-email">{L.email}</label>
                  <input
                    type="email"
                    id="lg-fp-email"
                    className="lg-input"
                    value={fpEmail}
                    onChange={(e) => setFpEmail(e.target.value)}
                    placeholder={L.emailPh}
                    autoComplete="email"
                    autoFocus
                  />
                </div>
                {fpState === 'error' && <div className="lg-error" role="alert">{fpError}</div>}
                <div className="lg-fp-row">
                  <button type="button" className="lg-linkbtn" onClick={() => setFpOpen(false)}>{L.cancel}</button>
                  <button type="submit" className="lg-btn" disabled={fpState === 'sending'}>
                    {fpState === 'sending' ? L.fpSending : L.fpSend}
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>
      )}
    </main>
  );
}

function Arrow() {
  return (
    <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M3 8h10M9 4l4 4-4 4" />
    </svg>
  );
}

export default LoginPage;
